import type { CrmContactRow } from "../../@types/webotServer";

/**
 * "IS THIS LISTING'S OWNER ALREADY ONE OF MY CONTACTS?" — asked by the LISTING, answered once
 * for every surface on the screen (Trello #254).
 *
 * ── Why the listing, and not the phone ──────────────────────────────────────────────
 *
 * The listings board's save-owner button used to ask only `useContactByPhone(<the ad's phone>)`.
 * Most Yad2 ads carry a 073/055 mask, the save dialog asks the realtor to type the owner's real
 * number, and the contact is written under THAT number — which nothing asked about again. So
 * after a reload the button offered "save" once more, and a second save made a second contact.
 * Measured 2026-11-03: 37 of the 52 owners saved on a market ad were invisible to the button,
 * every one behind a mask. The save also links the contact to the listing
 * (`sellingListingIds` / `rentingOutListingIds`), and the link does not move, so this store asks
 * by it: `POST /contacts/listing-sellers` returns the caller's own rows that link a listing.
 *
 * ── Three states, as in `contactLookupStore` ────────────────────────────────────────
 *
 *   undefined   nobody has an answer yet (or the request FAILED) → claim nothing
 *   []          answered: no contact of mine is linked to it     → may offer "save"
 *   [contact…]  answered: these contacts are its seller side     → show them
 *
 * A failed request releases its ids back to `undefined` and is remembered as FAILED, never as
 * `[]`: "nobody of yours" is the answer a screen acts on (it offers to create a contact), and a
 * network blip is not evidence for it. A caller that must still render something after a
 * failure reads `hasFailed` and decides for itself.
 *
 * ── Freshness ───────────────────────────────────────────────────────────────────────
 *
 * An answer is re-asked once it is older than a minute and a reader asks again, so a contact
 * linked on another screen (the contact card's "הנכס למכירה" picker, another tab) shows up by
 * the next navigation. A save in THIS browser does not wait: `add` writes it through.
 *
 * ── It belongs to ONE account ───────────────────────────────────────────────────────
 *
 * `bindAccount` drops everything when the signed-in user changes; an answer for the previous
 * account that lands late is discarded by its `session`. Same reasoning as the lookup store.
 *
 * Pure: no React, no network, an injected clock. `createListingSellerStore` exists for the
 * tests; the app uses the one instance exported at the bottom.
 */

/** What a surface needs to name the saved owner and open their card. Never a contact record. */
export type ListingSellerContact = {
  /** The contact's document id — the phone in E.164. */
  contactId: string;
  name?: string;
};

/** An answered listing. `undefined` (no entry) is "not answered", which is not a value. */
export type ListingSellersValue = readonly ListingSellerContact[];

/** How long an answer is trusted before the next reader re-asks. */
export const LISTING_SELLERS_STALE_AFTER_MS = 60 * 1000;

type Entry = { value: ListingSellersValue; at: number };

export type ListingSellerStore = {
  /** Idempotent. A CHANGE of account drops every answer and starts a new session. */
  bindAccount: (account: string | null) => void;
  /** Identifies the bound account's lifetime. Capture it when asking; pass it back with the answer. */
  session: () => number;
  read: (listingId: string) => ListingSellersValue | undefined;
  isInFlight: (listingId: string) => boolean;
  /** The last request for this listing failed and nothing is held or on its way. */
  hasFailed: (listingId: string) => boolean;
  /**
   * Which of these listings need asking — unknown or stale, and not already being asked. Marks
   * them in flight in the same pass, so the row and the grid card of one listing ask once.
   */
  claim: (listingIds: readonly string[]) => string[];
  /** The answer to a claim: every listing asked about is settled — its linked rows, or `[]`. */
  record: (session: number, askedIds: readonly string[], rows: readonly CrmContactRow[]) => void;
  /** A claim that failed: back to "not answered", marked failed — never `[]`. */
  release: (session: number, askedIds: readonly string[]) => void;
  /** Write-through of a save made in this browser: this contact is now linked to this listing. */
  add: (listingId: string, contact: ListingSellerContact) => void;
  subscribe: (listener: () => void) => () => void;
};

const clean = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/** The row's display name: the stored full name, else first + last. */
const nameOf = (row: CrmContactRow): string | undefined =>
  clean(row.name) ?? clean([clean(row.firstName), clean(row.lastName)].filter(Boolean).join(" "));

/** Does this row link the listing — as its seller (sale) or as its landlord (rent)? */
const linksListing = (row: CrmContactRow, listingId: string): boolean =>
  (row.sellingListingIds ?? []).includes(listingId) || (row.rentingOutListingIds ?? []).includes(listingId);

export const createListingSellerStore = (now: () => number = Date.now): ListingSellerStore => {
  let boundAccount: string | null = null;
  let currentSession = 0;
  const entries = new Map<string, Entry>();
  const inFlight = new Set<string>();
  const failed = new Set<string>();
  const subscribers = new Set<() => void>();

  const notify = (): void => subscribers.forEach((listener) => listener());

  return {
    bindAccount: (account) => {
      const next = account?.trim().toLowerCase() || null;
      if (next === boundAccount) return;
      boundAccount = next;
      currentSession += 1;
      entries.clear();
      inFlight.clear();
      failed.clear();
      // No `notify()`: this runs while a reader is rendering, and every reader re-renders on
      // the same auth change that brought it here.
    },

    session: () => currentSession,

    read: (listingId) => entries.get(listingId)?.value,

    isInFlight: (listingId) => inFlight.has(listingId),

    hasFailed: (listingId) => failed.has(listingId) && !inFlight.has(listingId) && !entries.has(listingId),

    claim: (listingIds) => {
      const claimed: string[] = [];
      const staleBefore = now() - LISTING_SELLERS_STALE_AFTER_MS;
      for (const listingId of listingIds) {
        if (!listingId || inFlight.has(listingId)) continue;
        const entry = entries.get(listingId);
        if (entry && entry.at > staleBefore) continue;
        inFlight.add(listingId);
        claimed.push(listingId);
      }
      return claimed;
    },

    record: (session, askedIds, rows) => {
      if (session !== currentSession) return;
      const at = now();
      for (const listingId of askedIds) {
        // A write-through landed while the request was out: it is newer than this answer.
        if (!inFlight.delete(listingId)) continue;
        failed.delete(listingId);
        const linked = rows
          .filter((row) => linksListing(row, listingId))
          .map((row) => ({ contactId: row.contactId, name: nameOf(row) }))
          .sort((a, b) => a.contactId.localeCompare(b.contactId));
        entries.set(listingId, { value: linked, at });
      }
      notify();
    },

    release: (session, askedIds) => {
      if (session !== currentSession) return;
      let released = false;
      for (const listingId of askedIds) {
        if (!inFlight.delete(listingId)) continue;
        released = true;
        failed.add(listingId);
      }
      if (released) notify();
    },

    add: (listingId, contact) => {
      if (!listingId || !contact.contactId) return;
      const held = entries.get(listingId)?.value ?? [];
      const others = held.filter((existing) => existing.contactId !== contact.contactId);
      inFlight.delete(listingId);
      failed.delete(listingId);
      entries.set(listingId, { value: [contact, ...others], at: now() });
      notify();
    },

    subscribe: (listener) => {
      subscribers.add(listener);
      return () => {
        subscribers.delete(listener);
      };
    }
  };
};

/** The app's one instance. */
export const listingSellerStore = createListingSellerStore();
